const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

const token = () => localStorage.getItem("token");
export const setToken = (t) => t ? localStorage.setItem("token", t) : localStorage.removeItem("token");

async function req(path, { method = "GET", body, form } = {}) {
  const headers = {};
  if (token()) headers["Authorization"] = "Bearer " + token();
  const opts = { method, headers };
  if (form) {
    opts.body = form; // URLSearchParams -> x-www-form-urlencoded
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const r = await fetch(API_BASE + path, opts);
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).detail || detail; } catch (e) {}
    const err = new Error(typeof detail === "string" ? detail : "Request failed");
    err.status = r.status;
    throw err;
  }
  if (r.status === 204) return null;
  return r.json();
}

export const api = {
  login: (email, password) => {
    const f = new URLSearchParams();
    f.set("username", email);
    f.set("password", password);
    return req("/auth/login", { method: "POST", form: f });
  },
  me: () => req("/auth/me"),

  // admin — courses authoring
  courses: () => req("/admin/courses"),
  usersList: () => req("/admin/users"),
  createUser: (b) => req("/admin/users", { method: "POST", body: b }),
  deleteUser: (id) => req(`/admin/users/${id}`, { method: "DELETE" }),
  changePassword: (b) => req("/auth/change-password", { method: "POST", body: b }),
  uploadAdminFile: async (fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    const h = {}; if (token()) h["Authorization"] = "Bearer " + token();
    const r = await fetch(API_BASE + "/admin/files", { method: "POST", headers: h, body: fd });
    if (!r.ok) { let d = "Upload failed"; try { d = (await r.json()).detail || d; } catch (e) {} throw new Error(d); }
    return r.json();
  },
  uploadFinal: async (fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    const h = {}; if (token()) h["Authorization"] = "Bearer " + token();
    const r = await fetch(API_BASE + "/me/final/upload", { method: "POST", headers: h, body: fd });
    if (!r.ok) { let d = "Upload failed"; try { d = (await r.json()).detail || d; } catch (e) {} throw new Error(d); }
    return r.json();
  },
  updateAssignedCourses: (uid, courseIds) => req("/admin/users/" + uid + "/assigned-courses", { method: "PATCH", body: { course_ids: courseIds } }),
  updateUser: (uid, data) => req(`/admin/users/${uid}`, { method: "PATCH", body: data }),
  bulkQuestions: async (subId, fileObj, mode = "replace", questionSet = "A") => {
    const fd = new FormData(); fd.append("file", fileObj);
    const h = {}; if (token()) h["Authorization"] = "Bearer " + token();
    const r = await fetch(API_BASE + `/admin/sub-modules/${subId}/questions/bulk?mode=${mode}&question_set=${questionSet}`, { method: "POST", headers: h, body: fd });
    if (!r.ok) { let d = "Upload failed"; try { d = (await r.json()).detail || d; } catch (e) {} throw new Error(d); }
    return r.json();
  },
  bulkFinalQuestions: async (cid, fileObj, mode = "replace") => {
    const fd = new FormData(); fd.append("file", fileObj);
    const h = {}; if (token()) h["Authorization"] = "Bearer " + token();
    const r = await fetch(API_BASE + `/admin/courses/${cid}/final-questions/bulk?mode=${mode}`, { method: "POST", headers: h, body: fd });
    if (!r.ok) { let d = "Upload failed"; try { d = (await r.json()).detail || d; } catch (e) {} throw new Error(d); }
    return r.json();
  },

  createCourse: (b) => req("/admin/courses", { method: "POST", body: b }),
  courseDetail: (id) => req(`/admin/courses/${id}`),
  patchCourse: (id, b) => req(`/admin/courses/${id}`, { method: "PATCH", body: b }),
  deleteCourse: (id) => req(`/admin/courses/${id}`, { method: "DELETE" }),
  addModule: (cid, b) => req(`/admin/courses/${cid}/modules`, { method: "POST", body: b }),
  patchModule: (id, b) => req(`/admin/modules/${id}`, { method: "PATCH", body: b }),
  deleteModule: (id) => req(`/admin/modules/${id}`, { method: "DELETE" }),
  reorderModules: (cid, ids) => req(`/admin/courses/${cid}/reorder`, { method: "POST", body: { module_ids: ids } }),
  addSubModule: (mid, b) => req(`/admin/modules/${mid}/sub-modules`, { method: "POST", body: b }),
  patchSubModule: (id, b) => req(`/admin/sub-modules/${id}`, { method: "PATCH", body: b }),
  deleteSubModule: (id) => req(`/admin/sub-modules/${id}`, { method: "DELETE" }),
  reorderSubModules: (mid, ids) => req(`/admin/modules/${mid}/reorder-subs`, { method: "POST", body: { sub_module_ids: ids } }),
  addQuestion: (sid, b) => req(`/admin/sub-modules/${sid}/questions`, { method: "POST", body: b }),
  patchQuestion: (id, b) => req(`/admin/questions/${id}`, { method: "PATCH", body: b }),
  deleteQuestion: (id) => req(`/admin/questions/${id}`, { method: "DELETE" }),
  addFinalQuestion: (cid, b) => req(`/admin/courses/${cid}/final-questions`, { method: "POST", body: b }),
  patchFinalQuestion: (id, b) => req(`/admin/final-questions/${id}`, { method: "PATCH", body: b }),
  deleteFinalQuestion: (id) => req(`/admin/final-questions/${id}`, { method: "DELETE" }),
  finalQuiz: () => req(`/me/final/quiz`),
  submitFinalQuiz: (answers) => req(`/me/final/quiz/submit`, { method: "POST", body: { answers } }),

  // admin — students
  createStudent: (b) => req("/admin/students", { method: "POST", body: b }),
  students: () => req("/admin/students"),
  extend: (id, days) => req(`/admin/students/${id}/extend`, { method: "PATCH", body: { days } }),
  setStatus: (id, status) => req(`/admin/students/${id}/status`, { method: "PATCH", body: { status } }),
  studentDetail: (id) => req(`/admin/students/${id}/detail`),
  decideAssessment: (id, b) => req(`/admin/students/${id}/assessment`, { method: "POST", body: b }),
  certRequests: () => req("/admin/cert-requests"),
  dismissCertRequest: (id) => req(`/admin/cert-requests/${id}`, { method: "DELETE" }),
  myNotifications: () => req("/me/notifications"),
  readAllNotifications: () => req("/me/notifications/read-all", { method: "POST" }),
  applyCertificate: (b) => req("/me/certificate-request", { method: "POST", body: b }),
  adminSessions: () => req("/admin/sessions"),
  decideSession: (id, b) => req(`/admin/sessions/${id}/decision`, { method: "POST", body: b }),

  // student
  myCourse: () => req("/me/course"),
  completeVideo: (sid) => req(`/me/sub-modules/${sid}/video-complete`, { method: "POST" }),
  quiz: (sid) => req(`/me/sub-modules/${sid}/quiz`),
  submitQuiz: (sid, answers) => req(`/me/sub-modules/${sid}/quiz/submit`, { method: "POST", body: { answers } }),
  mySessions: () => req("/me/sessions"),
  submitFeedback: (b) => req("/me/feedback", { method: "POST", body: b }),
  submitFinal: (b) => req("/me/final/submit", { method: "POST", body: b }),
  fileUrl: (u) => (u && u.startsWith("/api/files/")) ? (u + "?token=" + (token() || "")) : u,
  requestCertificate: () => req("/me/certificate/request", { method: "POST" }),
  approveCertificate: (uid) => req(`/admin/students/${uid}/certificate`, { method: "POST" }),
  setProjectRequirement: (uid, project_required) => req(`/admin/students/${uid}/project-requirement`, { method: "PATCH", body: { project_required } }),
  setStudentLock: (uid, module_id, days) => req(`/admin/students/${uid}/lock`, { method: "PATCH", body: { module_id, days } }),
  myCourses: () => req("/me/courses"),
  myCourseById: (cid) => req(`/me/course?course_id=${cid}`),
  studentEnrollments: (uid) => req(`/admin/students/${uid}/enrollments`),
  enrollStudent: (uid, course_id) => req(`/admin/students/${uid}/enroll`, { method: "POST", body: { course_id } }),
  unenrollStudent: (uid, course_id) => req(`/admin/students/${uid}/enroll/${course_id}`, { method: "DELETE" }),
  listPrivileges: () => req("/admin/privileges"),
  listRoles: () => req("/admin/roles"),
  createRole: (name, privileges) => req("/admin/roles", { method: "POST", body: { name, privileges } }),
  updateRole: (id, data) => req(`/admin/roles/${id}`, { method: "PATCH", body: data }),
  deleteRole: (id) => req(`/admin/roles/${id}`, { method: "DELETE" }),
  assignRole: (uid, role_id) => req(`/admin/users/${uid}/role`, { method: "PATCH", body: { role_id } }),
  trainerCourse: (cid) => req(`/admin/trainer/course/${cid}`),
  duplicateCourse: (cid) => req(`/admin/courses/${cid}/duplicate`, { method: "POST" }),
  setStudentPause: (uid, paused) => req(`/admin/students/${uid}/pause`, { method: "PATCH", body: { paused } }),
  pendingCertRequests: () => req("/admin/certificate-requests"),
  requestSession: (b) => req("/me/sessions/request", { method: "POST", body: b }),

  // assignments
  getAssignment: (subId) => req(`/me/sub-modules/${subId}/assignment`),
  uploadAssignment: async (subId, fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    const h = {}; if (token()) h["Authorization"] = "Bearer " + token();
    const r = await fetch(API_BASE + `/me/sub-modules/${subId}/assignment/upload`, { method: "POST", headers: h, body: fd });
    if (!r.ok) { let d = "Upload failed"; try { d = (await r.json()).detail || d; } catch (e) {} throw new Error(d); }
    return r.json();
  },
  courseAssignments: (cid) => req(`/admin/courses/${cid}/assignments`),
  reviewAssignment: (id, action, feedback = "") => req(`/admin/assignments/${id}`, { method: "PATCH", body: { action, feedback } }),
};
